import React, { useMemo, useState } from 'react';
import { ApiQueryTemplate, ApiQueryTemplateStatusEnum } from '../../api';
import QueryTemplatesModal from './QueryTemplatesModal';
import { querytemplateApi } from '../../apis';
import { SalvLabel, SalvSearchField, SalvTable, SalvTablePaginationType, Size } from '@salvapp/ui-components';
import SalvPage from '../../common/SalvPage';
import { getConfigurationUrl } from '../../navigation/helper';
import { ColumnHelper, createColumnHelper } from '@tanstack/react-table';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useMe } from '../../organisation/useMe';
import { CAN_ADD_QUERY_TEMPLATE, CAN_MODIFY_QUERY_TEMPLATE, CAN_READ_QUERY_TEMPLATE } from '../../auth/permissions';
import Has from '../../auth/Has';
import './QueryTemplates.scss';
import { ConfigurationProduct } from '../../settings/configuration/Configuration';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { QUERY_TEMPLATE_KEY } from './queryTemplatesActions';

const DEFAULT_PAGE_SIZE = 20;

const fetchQueryTemplates = (page: number, query?: string) => {
  return querytemplateApi.findQueryTemplates({ page, size: DEFAULT_PAGE_SIZE, query });
};
export const commaSeparatedStringsRegex = /^('([^']|'')*'(,\s*'([^']|'')*')*)?$/;
export const contentToList = (content?: string) =>
  content ? content.split(/'\s*,\s*'/g).map((item) => item.replace(/(^')|('$)/g, '')) : [];

const getStatus = (status: ApiQueryTemplateStatusEnum | undefined) => {
  switch (status) {
    case ApiQueryTemplateStatusEnum.ACTIVE:
      return <SalvLabel variant='success'>Active</SalvLabel>;
    case ApiQueryTemplateStatusEnum.DRAFT:
      return <SalvLabel variant='warning'>Pending</SalvLabel>;
  }
};

const name = (columnHelper: ColumnHelper<ApiQueryTemplate>) =>
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue }) => getValue(),
  });

const content = (columnHelper: ColumnHelper<ApiQueryTemplate>) =>
  columnHelper.accessor('content', {
    header: 'Content',
    cell: ({ getValue }) => (
      <div className='content-cell'>
        {commaSeparatedStringsRegex.test(getValue())
          ? contentToList(getValue()).map((item, index) => (
              <SalvLabel variant='secondary' key={index} className='m-a-4 d-inline-block'>
                {item}
              </SalvLabel>
            ))
          : getValue()}
      </div>
    ),
    size: 80,
  });

const actions = (columnHelper: ColumnHelper<ApiQueryTemplate>) =>
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 0,
    cell: ({ row }) => (
      <Has allPermissions={[CAN_READ_QUERY_TEMPLATE]}>
        <QueryTemplatesModal
          queryTemplate={row.original}
          buttonLabel={
            <Has allPermissions={[CAN_MODIFY_QUERY_TEMPLATE]}>
              {(hasPermission) => (hasPermission ? 'Edit' : 'View')}
            </Has>
          }
          title={
            <Has allPermissions={[CAN_MODIFY_QUERY_TEMPLATE]}>
              {(hasPermission) => (hasPermission ? 'Edit query template' : 'View query template')}
            </Has>
          }
        />
      </Has>
    ),
  });

const status = (columnHelper: ColumnHelper<ApiQueryTemplate>) =>
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => getStatus(getValue()),
  });

export function QueryTemplates() {
  const { currentOrganisation } = useMe();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const columnHelper = createColumnHelper<ApiQueryTemplate>();
  const fourEyesEnabled = isFeatureEnabled(
    ORGANISATION_FEATURE.FOUR_EYES_QUERY_TEMPLATES,
    currentOrganisation?.features || []
  );

  const { data, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_TEMPLATE_KEY, currentOrganisation?.id, query],
    queryFn: ({ pageParam }) => fetchQueryTemplates(pageParam, query),
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage) => lastPage.number + 1,
    initialPageParam: 0,
  });

  const queryTemplateColumns = useMemo(() => {
    if (fourEyesEnabled) {
      return [name(columnHelper), content(columnHelper), status(columnHelper), actions(columnHelper)];
    } else {
      return [name(columnHelper), content(columnHelper), actions(columnHelper)];
    }
  }, [fourEyesEnabled, columnHelper]);

  return (
    <SalvPage
      title={'Query templates'}
      className='query-templates-page'
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.GENERAL),
      }}
    >
      <SalvTable
        columns={queryTemplateColumns}
        data={data?.pages.flatMap((page) => page.content) || []}
        pagination={{
          type: SalvTablePaginationType.LOAD_MORE,
          total: data?.pages[0].total || 0,
          state: {
            pageIndex: data ? data.pages.length - 1 : 0,
            pageSize: DEFAULT_PAGE_SIZE,
          },
          setPagination: () => fetchNextPage(),
        }}
        emptyMessage='No query templates yet'
        title='Query Templates'
        actions={
          <>
            <SalvSearchField
              key='search'
              value={query}
              onSubmit={() => {}}
              onChange={(value: string) => {
                setQuery(value || undefined);
              }}
              placeholder='Search by name'
              size={Size.SMALL}
              ariaLabel='Search query templates'
            />

            <Has allPermissions={CAN_ADD_QUERY_TEMPLATE}>
              <QueryTemplatesModal queryTemplate={null} buttonLabel='Add query template' title='Add query template' />
            </Has>
          </>
        }
      />
    </SalvPage>
  );
}

export default QueryTemplates;
